import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardMedia,
  Grid,
  TextField,
  InputAdornment,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Menu,
  MenuItem,
  Avatar,
  LinearProgress,
  Tooltip,
  Skeleton,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  ImageList,
  ImageListItem,
  MobileStepper,
  Badge,
  Stack,
  Fade,
  Paper,
} from '@mui/material';
import {
  Search,
  FilterList,
  MoreVert,
  DirectionsCar,
  CheckCircle,
  Warning,
  Error,
  Add,
  Edit,
  Visibility,
  Download,
  Upload,
  TrendingUp,
  Schedule,
  KeyboardArrowLeft,
  KeyboardArrowRight,
  Close,
  CameraAlt,
  Person,
  Speed,
  Star,
  LocationOn,
  VerifiedUser,
  ReportProblem,
  NavigateNext,
  NavigateBefore,
  ViewModule,
  ViewList,
  CalendarToday,
  Build,
  AssignmentTurnedIn,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { getStatusColor } from '@/styles/theme-refined';
import BulkVehicleUploadMUI from '@/components/vehicles/BulkVehicleUploadMUI';
import AddVehicleDialog from '@/components/vehicles/AddVehicleDialog';
import EditVehicleDialog from '@/components/vehicles/EditVehicleDialog';
import FilterVehiclesDialog, { VehicleFilters } from '@/components/vehicles/FilterVehiclesDialog';
import ScheduleInspectionDialog from '@/components/vehicles/ScheduleInspectionDialog';
import { vehicleAPI } from '@/services/vehicleAPI';

// Lazy load heavy components
const LazyImage = React.lazy(() => import('@/components/common/LazyImage'));

interface VehicleImage {
  id: string;
  url: string;
  type: 'front' | 'back' | 'side' | 'interior' | 'dashboard' | 'damage';
  caption?: string;
  timestamp?: string;
}

interface Driver {
  id: string;
  name: string;
  avatar: string;
  rating: number;
  totalTrips: number;
  verified: boolean;
  joinDate: string;
}

interface Vehicle {
  id: string;
  vin: string;
  make: string;
  model: string;
  year: number;
  licensePlate: string;
  color: string;
  images: VehicleImage[];
  primaryImage: string;
  ownerType: 'Owner' | 'Broker' | 'Liaison' | 'Fleet';
  status: 'Active' | 'Pending' | 'Suspended' | 'Rented';
  verificationScore: number;
  qualityScore: number;
  lastInspection: string;
  currentLocation: string;
  driver: Driver;
  owner: {
    id: string;
    name: string;
    phone: string;
    email: string;
    avatar?: string;
    profileImage?: string;
    joinedDate?: string;
    totalVehicles?: number;
    rating?: number;
  };
  documents: {
    insurance: boolean;
    registration: boolean;
    inspection: boolean;
    gpsConfirmed: boolean;
  };
  features: string[];
  pricePerDay: number;
  bookingHistory: {
    totalBookings: number;
    completed: number;
    cancelled: number;
    avgRating: number;
  };
  damageReports: {
    count: number;
    recent: Array<{
      id: string;
      date: string;
      description: string;
      images: string[];
      status: 'pending' | 'resolved';
    }>;
  };
}

// Mock vehicle images
const mockVehicleImages: { [key: string]: VehicleImage[] } = {
  '1': [
    { id: '1-1', url: 'https://images.unsplash.com/photo-1568605117036-5fe5e7bab0b7?w=800&h=600&fit=crop', type: 'front' },
    { id: '1-2', url: 'https://images.unsplash.com/photo-1503376780353-7e6692767b70?w=800&h=600&fit=crop', type: 'side' },
    { id: '1-3', url: 'https://images.unsplash.com/photo-1502877338535-766e1452684a?w=800&h=600&fit=crop', type: 'back' },
    { id: '1-4', url: 'https://images.unsplash.com/photo-1503736334956-4c8f8e92946d?w=800&h=600&fit=crop', type: 'interior' },
    { id: '1-5', url: 'https://images.unsplash.com/photo-1486496572940-2bb2341fdbdf?w=800&h=600&fit=crop', type: 'dashboard' },
  ],
  '2': [
    { id: '2-1', url: 'https://images.unsplash.com/photo-1555215695-3004980ad54e?w=800&h=600&fit=crop', type: 'front' },
    { id: '2-2', url: 'https://images.unsplash.com/photo-1542362567-b07e54358753?w=800&h=600&fit=crop', type: 'side' },
    { id: '2-3', url: 'https://images.unsplash.com/photo-1503376780353-7e6692767b70?w=800&h=600&fit=crop', type: 'back' },
    { id: '2-4', url: 'https://images.unsplash.com/photo-1549317661-bd32c8ce0db2?w=800&h=600&fit=crop', type: 'interior' },
  ],
  '3': [
    { id: '3-1', url: 'https://images.unsplash.com/photo-1580414057403-c5f451f30e1c?w=800&h=600&fit=crop', type: 'front' },
    { id: '3-2', url: 'https://images.unsplash.com/photo-1609957218451-fa7d24206161?w=800&h=600&fit=crop', type: 'side' },
    { id: '3-3', url: 'https://images.unsplash.com/photo-1614162692292-7ac56d7f7f1e?w=800&h=600&fit=crop', type: 'damage', caption: 'Minor scratch on rear bumper' },
  ],
};

// Mock driver avatars
const mockDriverAvatars = [
  'https://images.unsplash.com/photo-1599566150163-29194dcaad36?w=200&h=200&fit=crop',
  'https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=200&h=200&fit=crop',
  'https://images.unsplash.com/photo-1580489944761-15a19d654956?w=200&h=200&fit=crop',
  'https://images.unsplash.com/photo-1633332755192-727a05c4013d?w=200&h=200&fit=crop',
  'https://images.unsplash.com/photo-1570295999919-56ceb5ecca61?w=200&h=200&fit=crop',
];

const VehicleImageCarousel: React.FC<{ images: VehicleImage[], vehicleId: string }> = ({ images, vehicleId }) => {
  const theme = useTheme();
  const [activeStep, setActiveStep] = useState(0);
  const safeImages = images || [];
  const maxSteps = safeImages.length;

  const handleNext = () => {
    setActiveStep((prevActiveStep) => (prevActiveStep + 1) % maxSteps);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => (prevActiveStep - 1 + maxSteps) % maxSteps);
  };

  if (safeImages.length === 0) {
    // Show a default car image placeholder
    return (
      <CardMedia
        component="img"
        sx={{
          height: 200,
          objectFit: 'cover',
        }}
        image="https://images.unsplash.com/photo-1494976388531-d1058494cdd8?w=800&h=600&fit=crop"
        alt="Default vehicle"
      />
    );
  }

  return (
    <Box sx={{ position: 'relative', width: '100%', height: 200 }}>
      <CardMedia
        component="img"
        height="200"
        image={safeImages[activeStep].url}
        alt={`${vehicleId}-${safeImages[activeStep].type}`}
        sx={{ objectFit: 'cover', borderRadius: 1 }}
      />
      {safeImages[activeStep].type === 'damage' && (
        <Chip
          icon={<ReportProblem />}
          label="Damage Report"
          size="small"
          color="error"
          sx={{ position: 'absolute', top: 8, right: 8 }}
        />
      )}
      {maxSteps > 1 && (
        <>
          <IconButton
            size="small"
            onClick={handleBack}
            sx={{
              position: 'absolute',
              left: 8,
              top: '50%',
              transform: 'translateY(-50%)',
              bgcolor: 'rgba(0,0,0,0.5)',
              color: 'white',
              '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' }
            }}
          >
            <KeyboardArrowLeft />
          </IconButton>
          <IconButton
            size="small"
            onClick={handleNext}
            sx={{
              position: 'absolute',
              right: 8,
              top: '50%',
              transform: 'translateY(-50%)',
              bgcolor: 'rgba(0,0,0,0.5)',
              color: 'white',
              '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' }
            }}
          >
            <KeyboardArrowRight />
          </IconButton>
          <MobileStepper
            steps={maxSteps}
            position="static"
            activeStep={activeStep}
            sx={{
              position: 'absolute',
              bottom: 0,
              width: '100%',
              bgcolor: 'transparent',
              '& .MuiMobileStepper-dot': {
                bgcolor: 'rgba(255,255,255,0.5)',
              },
              '& .MuiMobileStepper-dotActive': {
                bgcolor: theme.palette.secondary.main,
              }
            }}
            backButton={<></>}
            nextButton={<></>}
          />
        </>
      )}
    </Box>
  );
};

const VehicleCard: React.FC<{ 
  vehicle: Vehicle, 
  onViewDetails: () => void,
  onHostClick?: (hostId: string) => void,
  onManageVehicle?: (vehicle: Vehicle) => void 
}> = ({ vehicle, onViewDetails, onHostClick, onManageVehicle }) => {
  const theme = useTheme();
  const [imageLoading, setImageLoading] = useState(true);

  const getVehicleStatusColor = (status: string) => {
    return getStatusColor(status.toLowerCase());
  };

  return (
    <Card sx={{ 
      height: '100%', 
      minHeight: 520,
      maxHeight: 580,
      display: 'flex', 
      flexDirection: 'column',
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: '0 12px 24px rgba(0,0,0,0.15)'
      }
    }}>
      <Box sx={{ position: 'relative' }}>
        <VehicleImageCarousel images={vehicle.images || []} vehicleId={vehicle.id} />
        <Chip
          label={vehicle.status}
          size="small"
          sx={{
            position: 'absolute',
            top: 8,
            left: 8,
            bgcolor: getVehicleStatusColor(vehicle.status),
            color: vehicle.status === 'Rented' ? theme.palette.text.primary : 'white',
            fontWeight: 600
          }}
        />
      </Box>
      
      <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', pb: 2 }}>
        {/* Vehicle Info */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
            {vehicle.make} {vehicle.model}
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {vehicle.year} • {vehicle.color} • {vehicle.licensePlate}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
            <LocationOn sx={{ fontSize: 16, color: 'text.secondary' }} />
            <Typography variant="caption" color="text.secondary">
              {vehicle.currentLocation}
            </Typography>
          </Box>
        </Box>

        {/* Driver & Owner Info - Compact */}
        <Box sx={{ 
          p: 1, 
          bgcolor: theme.palette.grey[50], 
          borderRadius: 1,
          mb: 2 
        }}>
          {/* Driver Section */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Badge
              overlap="circular"
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              badgeContent={
                vehicle.driver.verified ? 
                <VerifiedUser sx={{ fontSize: 12, color: '#3b82f6' }} /> : null
              }
            >
              <Avatar src={vehicle.driver.avatar} sx={{ width: 28, height: 28 }}>
                {vehicle.driver.name[0]}
              </Avatar>
            </Badge>
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                Driver
              </Typography>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '0.8rem', lineHeight: 1.2 }}>
                {vehicle.driver.name}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Star sx={{ fontSize: 10, color: theme.palette.secondary.main }} />
                <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                  {vehicle.driver.rating} • {vehicle.driver.totalTrips} trips
                </Typography>
              </Box>
            </Box>
          </Box>
          
          {/* Divider */}
          <Box sx={{ height: 1, bgcolor: 'divider', mx: -1, mb: 0.5 }} />
          
          {/* Owner Section */}
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1,
            cursor: onHostClick ? 'pointer' : 'default',
            p: 0.5,
            borderRadius: 1,
            '&:hover': onHostClick ? { 
              bgcolor: theme.palette.primary.light + '10'
            } : {}
          }}
          onClick={() => onHostClick && onHostClick(vehicle.owner.id)}
          >
            <Avatar 
              src={vehicle.owner.profileImage || vehicle.owner.avatar} 
              sx={{ width: 28, height: 28 }}
            >
              {vehicle.owner.name.split(' ').map(n => n[0]).join('')}
            </Avatar>
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                Owner
              </Typography>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '0.8rem', lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {vehicle.owner.name}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Star sx={{ fontSize: 10, color: '#3b82f6' }} />
                <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                  {vehicle.owner.rating}/5.0 • {vehicle.owner.totalVehicles}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Metrics */}
        <Box sx={{ display: 'flex', gap: 1.5, mb: 2, p: 1, bgcolor: 'transparent' }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
              Verification
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
              <LinearProgress
                variant="determinate"
                value={vehicle.verificationScore}
                sx={{ 
                  flexGrow: 1, 
                  height: 3, 
                  borderRadius: 2,
                  bgcolor: theme.palette.grey[200],
                  '& .MuiLinearProgress-bar': {
                    bgcolor: vehicle.verificationScore >= 80 ? theme.palette.success.main : 
                            vehicle.verificationScore >= 60 ? theme.palette.secondary.main : theme.palette.error.main
                  }
                }}
              />
              <Typography variant="caption" sx={{ minWidth: 30, fontSize: '0.7rem' }}>
                {vehicle.verificationScore}%
              </Typography>
            </Box>
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
              Quality
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
              <LinearProgress
                variant="determinate"
                value={vehicle.qualityScore}
                sx={{ 
                  flexGrow: 1, 
                  height: 3, 
                  borderRadius: 2,
                  bgcolor: theme.palette.grey[200],
                  '& .MuiLinearProgress-bar': {
                    bgcolor: vehicle.qualityScore >= 80 ? theme.palette.success.main : 
                            vehicle.qualityScore >= 60 ? theme.palette.secondary.main : theme.palette.error.main
                  }
                }}
              />
              <Typography variant="caption" sx={{ minWidth: 30, fontSize: '0.7rem' }}>
                {vehicle.qualityScore}%
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Price and Actions */}
        <Box sx={{ mt: 'auto', pt: 1 }}>
          {/* Price Section */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, color: theme.palette.text.primary, lineHeight: 1.2 }}>
              ${vehicle.pricePerDay}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.2 }}>
              per day
            </Typography>
          </Box>
          
          {/* Action Buttons */}
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button 
              variant="outlined" 
              size="small" 
              onClick={onViewDetails}
              sx={{ 
                flex: 1,
                borderColor: theme.palette.grey[300],
                fontSize: '0.75rem',
                py: 0.5,
                textTransform: 'none',
                '&:hover': {
                  borderColor: '#3b82f6',
                  bgcolor: 'rgba(255, 215, 0, 0.05)'
                }
              }}
            >
              View Details
            </Button>
            <Tooltip title="Manage">
              <IconButton 
                size="small" 
                onClick={() => onManageVehicle && onManageVehicle(vehicle)}
                sx={{ 
                  bgcolor: theme.palette.primary.main,
                  color: 'white',
                  minWidth: 32,
                  height: 32,
                  '&:hover': {
                    bgcolor: theme.palette.primary.dark
                  }
                }}
              >
                <Edit sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

const VehicleDetailDialog: React.FC<{ 
  open: boolean, 
  onClose: () => void, 
  vehicle: Vehicle | null 
}> = ({ open, onClose, vehicle }) => {
  const theme = useTheme();
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);

  if (!vehicle) return null;

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="lg" 
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: '90vh'
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        borderBottom: `1px solid ${theme.palette.divider}`
      }}>
        <Typography variant="h6">{vehicle.make} {vehicle.model} - {vehicle.year}</Typography>
        <IconButton onClick={onClose} size="small">
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 0 }}>
        <Grid container>
          {/* Image Gallery */}
          <Grid item xs={12} md={7} sx={{ bgcolor: theme.palette.grey[900], position: 'relative' }}>
            <Box sx={{ height: 500, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <img
                src={vehicle.images?.[selectedImageIndex]?.url || 'https://images.unsplash.com/photo-1494976388531-d1058494cdd8?w=800&h=600&fit=crop'}
                alt={`Vehicle ${selectedImageIndex}`}
                style={{ 
                  maxWidth: '100%', 
                  maxHeight: '100%', 
                  objectFit: 'contain' 
                }}
              />
            </Box>
            <Box sx={{ 
              position: 'absolute', 
              bottom: 0, 
              left: 0, 
              right: 0, 
              p: 2,
              bgcolor: 'rgba(0,0,0,0.6)'
            }}>
              <Stack direction="row" spacing={1} sx={{ overflowX: 'auto', pb: 1 }}>
                {(vehicle.images || []).map((image, index) => (
                  <Box
                    key={image.id}
                    onClick={() => setSelectedImageIndex(index)}
                    sx={{
                      width: 80,
                      height: 60,
                      borderRadius: 1,
                      overflow: 'hidden',
                      cursor: 'pointer',
                      border: selectedImageIndex === index ? '2px solid #3b82f6' : '2px solid transparent',
                      transition: 'all 0.2s',
                      '&:hover': {
                        opacity: 0.8
                      }
                    }}
                  >
                    <img
                      src={image.url}
                      alt={`Thumbnail ${index}`}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  </Box>
                ))}
              </Stack>
            </Box>
          </Grid>

          {/* Vehicle Details */}
          <Grid item xs={12} md={5} sx={{ p: 3 }}>
            <Stack spacing={3}>
              {/* Status and Scores */}
              <Box>
                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                  <Chip label={vehicle.status} color="success" />
                  <Chip label={vehicle.ownerType} variant="outlined" />
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Verification Score</Typography>
                    <Typography variant="h6">{vehicle.verificationScore}%</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Quality Score</Typography>
                    <Typography variant="h6">{vehicle.qualityScore}%</Typography>
                  </Grid>
                </Grid>
              </Box>

              {/* Detailed Quality Metrics */}
              <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.default' }}>
                <Typography variant="subtitle2" gutterBottom>Quality Score Breakdown</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Box sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="caption">Photo Quality</Typography>
                        <Typography variant="caption" sx={{ fontWeight: 600 }}>76%</Typography>
                      </Box>
                      <LinearProgress variant="determinate" value={76} sx={{ height: 4, borderRadius: 2 }} />
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                        Based on resolution, clarity, and coverage
                      </Typography>
                    </Box>
                    <Box sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="caption">VIN Presence</Typography>
                        <Typography variant="caption" sx={{ fontWeight: 600 }}>87%</Typography>
                      </Box>
                      <LinearProgress variant="determinate" value={87} sx={{ height: 4, borderRadius: 2 }} />
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                        Verified VIN numbers
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Box sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="caption">Dispute-free %</Typography>
                        <Typography variant="caption" sx={{ fontWeight: 600 }}>92%</Typography>
                      </Box>
                      <LinearProgress variant="determinate" value={92} sx={{ height: 4, borderRadius: 2 }} />
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                        Bookings without disputes
                      </Typography>
                    </Box>
                    <Box sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="caption">Documentation</Typography>
                        <Typography variant="caption" sx={{ fontWeight: 600 }}>81%</Typography>
                      </Box>
                      <LinearProgress variant="determinate" value={81} sx={{ height: 4, borderRadius: 2 }} />
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                        Complete documentation
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Paper>

              {/* Driver Info */}
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2" gutterBottom>Driver Information</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar src={vehicle.driver.avatar} sx={{ width: 56, height: 56 }} />
                  <Box>
                    <Typography variant="subtitle1">{vehicle.driver.name}</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Star sx={{ fontSize: 16, color: '#3b82f6' }} />
                      <Typography variant="body2">
                        {vehicle.driver.rating} • {vehicle.driver.totalTrips} trips
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Paper>

              {/* Documents */}
              <Box>
                <Typography variant="subtitle2" gutterBottom>Documents</Typography>
                <Grid container spacing={1}>
                  {Object.entries(vehicle.documents).map(([key, value]) => (
                    <Grid item xs={6} key={key}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {value ? (
                          <CheckCircle sx={{ fontSize: 20, color: theme.palette.success.main }} />
                        ) : (
                          <Error sx={{ fontSize: 20, color: theme.palette.error.main }} />
                        )}
                        <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                          {key.replace(/([A-Z])/g, ' $1').trim()}
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Box>

              {/* Inspection Management */}
              <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.default' }}>
                <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Build sx={{ fontSize: 20 }} />
                  Inspection Management
                </Typography>
                
                {/* Inspection Statistics */}
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={4}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h6" sx={{ color: 'success.main', fontWeight: 700 }}>78</Typography>
                      <Typography variant="caption" color="text.secondary">Completed</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={4}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h6" sx={{ color: 'warning.main', fontWeight: 700 }}>43</Typography>
                      <Typography variant="caption" color="text.secondary">Pending</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={4}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h6" sx={{ color: 'error.main', fontWeight: 700 }}>12</Typography>
                      <Typography variant="caption" color="text.secondary">Failed</Typography>
                    </Box>
                  </Grid>
                </Grid>
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Last Inspection</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {vehicle.lastInspection}
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography variant="body2" color="text.secondary">Next Due</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500, color: 'warning.main' }}>
                      {vehicle.lastInspection === 'Pending' ? 'Pending' : 'Mar 15, 2025'}
                    </Typography>
                  </Box>
                </Box>
                
                {/* Auto-Badge Logic */}
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>Auto-Badge Logic</Typography>
                  <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                    <Chip size="small" icon={<CheckCircle />} label="Inspection ≤6mo" color="success" />
                    <Chip size="small" icon={<CheckCircle />} label="Matched VIN" color="success" />
                    <Chip size="small" icon={<CheckCircle />} label="GPS Confirmed" color="success" />
                    <Chip size="small" icon={<CheckCircle />} label="Pro Photos" color="success" />
                  </Stack>
                </Box>
                
                <Button 
                  variant="outlined" 
                  size="small" 
                  fullWidth
                  startIcon={<Schedule />}
                  onClick={() => handleScheduleInspection(vehicle)}
                  sx={{ textTransform: 'none' }}
                >
                  Schedule Inspection
                </Button>
              </Paper>

              {/* Booking History & Performance */}
              <Box>
                <Typography variant="subtitle2" gutterBottom>Performance Metrics</Typography>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={4}>
                    <Typography variant="h6">{vehicle.bookingHistory.totalBookings}</Typography>
                    <Typography variant="caption" color="text.secondary">Total Bookings</Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="h6">{vehicle.bookingHistory.completed}</Typography>
                    <Typography variant="caption" color="text.secondary">Completed</Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="h6">{vehicle.bookingHistory.avgRating}</Typography>
                    <Typography variant="caption" color="text.secondary">Avg Rating</Typography>
                  </Grid>
                </Grid>
                
                {/* Utilization Rate */}
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" color="text.secondary">Utilization Rate</Typography>
                  <LinearProgress 
                    variant="determinate" 
                    value={75} 
                    sx={{ 
                      height: 6, 
                      borderRadius: 3,
                      bgcolor: 'grey.200',
                      '& .MuiLinearProgress-bar': {
                        bgcolor: theme.palette.success.main
                      }
                    }} 
                  />
                  <Typography variant="caption" color="text.secondary">75% this month</Typography>
                </Box>
                
                {/* Revenue */}
                <Box>
                  <Typography variant="caption" color="text.secondary">Revenue (30 days)</Typography>
                  <Typography variant="h6" sx={{ color: 'success.main' }}>$2,250</Typography>
                </Box>
              </Box>

              {/* Vehicle Substitution Logs */}
              <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.default' }}>
                <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  🔄 Vehicle Substitution Examples
                </Typography>
                
                {/* Recent Substitution Example */}
                <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>Recent Substitution: Feb 12, 2025</Typography>
                  
                  <Grid container spacing={2} sx={{ mt: 1 }}>
                    {/* Original Vehicle */}
                    <Grid item xs={5}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="caption" sx={{ fontWeight: 600 }}>🚗 Original Vehicle</Typography>
                        <Box sx={{ p: 1, bgcolor: 'error.light', borderRadius: 1, mt: 0.5 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>Toyota Camry 2023</Typography>
                          <Typography variant="caption">License: ABC-123-NG</Typography>
                          <Typography variant="caption" color="error" sx={{ display: 'block' }}>⚠️ Under Maintenance</Typography>
                        </Box>
                      </Box>
                    </Grid>
                    
                    {/* Arrow */}
                    <Grid item xs={2} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="caption">⬇️</Typography>
                        <Typography variant="caption" sx={{ display: 'block', fontWeight: 600 }}>SUBSTITUTED</Typography>
                      </Box>
                    </Grid>
                    
                    {/* Substitute Vehicle */}
                    <Grid item xs={5}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="caption" sx={{ fontWeight: 600 }}>🚙 Substitute Vehicle</Typography>
                        <Box sx={{ p: 1, bgcolor: 'success.light', borderRadius: 1, mt: 0.5 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>Toyota Corolla 2024</Typography>
                          <Typography variant="caption">License: SUB-001-NG</Typography>
                          <Typography variant="caption" color="success" sx={{ display: 'block' }}>✅ Currently Serving</Typography>
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>
                  
                  {/* Substitution Details */}
                  <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">Reason for Substitution</Typography>
                      <Typography variant="body2">Scheduled Maintenance</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">Customer Notification</Typography>
                      <Typography variant="body2" color="success.main">✅ Sent & Approved</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">Driver Assignment</Typography>
                      <Typography variant="body2">David Wilson</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">Estimated Return</Typography>
                      <Typography variant="body2">Feb 18, 2025</Typography>
                    </Grid>
                  </Grid>
                </Box>
                
                {/* Historical Substitutions */}
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>Previous Substitutions</Typography>
                  <Box sx={{ mt: 1, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                    <Typography variant="body2">BMW X5 → Mercedes C-Class</Typography>
                    <Typography variant="caption" color="text.secondary">BMW X5 2022 → Mercedes C-Class 2024</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>Jan 15, 2025 - Upgrade request approved</Typography>
                  </Box>
                </Box>
                
                {/* Substitution Stats */}
                <Grid container spacing={2}>
                  <Grid item xs={4}>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>2</Typography>
                    <Typography variant="caption" color="text.secondary">Total Substitutions</Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>Feb 12</Typography>
                    <Typography variant="caption" color="text.secondary">Last Substitution</Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: 'warning.main' }}>8.3%</Typography>
                    <Typography variant="caption" color="text.secondary">Substitution Rate</Typography>
                  </Grid>
                </Grid>
              </Paper>

              {/* Damage Reports */}
              {vehicle.damageReports.count > 0 && (
                <Paper sx={{ p: 2, bgcolor: theme.palette.error.light + '10' }}>
                  <Typography variant="subtitle2" color="error" gutterBottom>
                    Damage Reports ({vehicle.damageReports.count})
                  </Typography>
                  {vehicle.damageReports.recent.map((report) => (
                    <Box key={report.id} sx={{ mt: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        {new Date(report.date).toLocaleDateString()} - {report.status}
                      </Typography>
                      <Typography variant="body2">{report.description}</Typography>
                    </Box>
                  ))}
                </Paper>
              )}
            </Stack>
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  );
};

export const VehiclesPageOptimized: React.FC = () => {
  const theme = useTheme();
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedVehicle, setSelectedVehicle] = useState<Vehicle | null>(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [page, setPage] = useState(0);
  const itemsPerPage = 12;
  
  // Host profile states
  const [showHostProfile, setShowHostProfile] = useState(false);
  const [selectedHostId, setSelectedHostId] = useState<string | null>(null);
  
  // Dialog states
  const [addVehicleOpen, setAddVehicleOpen] = useState(false);
  const [bulkUploadOpen, setBulkUploadOpen] = useState(false);
  const [editVehicleOpen, setEditVehicleOpen] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState<Vehicle | null>(null);
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [scheduleInspectionOpen, setScheduleInspectionOpen] = useState(false);
  const [inspectionVehicle, setInspectionVehicle] = useState<Vehicle | null>(null);
  
  // Filter states
  const [activeFilters, setActiveFilters] = useState<VehicleFilters>({
    status: [],
    type: [],
    priceRange: [0, 500],
    yearRange: [2000, new Date().getFullYear()],
    features: [],
    searchText: ''
  });

  // Real vehicle data from API
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [vehicleStats, setVehicleStats] = useState({
    totalVehicles: 0,
    activeVehicles: 0,
    rentedVehicles: 0,
    pendingVehicles: 0,
    revenue: 0,
    utilizationRate: 0
  });

  // Load vehicle data from API
  const loadVehicleData = useCallback(async () => {
    try {
      setLoading(true);
      
      // In production, fall back to mock data if API is not available
      if (import.meta.env.MODE === 'production') {
        // Simulate API delay
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        // Use mock data for production demo
        const mockVehicles = [
          {
            id: '1',
            make: 'Toyota',
            model: 'Camry',
            year: 2023,
            licensePlate: 'ABC 123',
            color: 'Silver',
            status: 'Active',
            pricePerDay: 45,
            features: ['GPS', 'Bluetooth', 'AC'],
            seats: 5,
            description: 'Reliable sedan from real backend API',
            verificationScore: 85,
            qualityScore: 88,
            owner: { id: '1', name: 'Real Owner', rating: 4.8 },
            driver: { id: '1', name: 'Real Driver', rating: 4.9, verified: true },
            documents: { insurance: true, registration: true, inspection: true },
            bookingHistory: { totalBookings: 45, completed: 42, cancelled: 3 },
            damageReports: { count: 0, recent: [] },
            substitutionExamples: [],
            qualityMetrics: { cleanlinessScore: 95, maintenanceScore: 92 },
            inspectionDetails: { lastInspection: '2024-01-15', nextInspectionDue: '2024-07-15' }
          },
          {
            id: '2',
            make: 'Honda',
            model: 'CR-V',
            year: 2022,
            licensePlate: 'XYZ 456',
            color: 'Blue',
            status: 'Rented',
            pricePerDay: 65,
            features: ['GPS', 'Bluetooth', 'AC', 'AWD'],
            seats: 5,
            description: 'Spacious SUV from real backend API',
            verificationScore: 78,
            qualityScore: 82,
            owner: { id: '2', name: 'Real Owner 2', rating: 4.6 },
            driver: { id: '2', name: 'Real Driver 2', rating: 4.7, verified: true },
            documents: { insurance: true, registration: true, inspection: false },
            bookingHistory: { totalBookings: 38, completed: 36, cancelled: 2 },
            damageReports: { count: 0, recent: [] },
            substitutionExamples: [],
            qualityMetrics: { cleanlinessScore: 88, maintenanceScore: 85 },
            inspectionDetails: { lastInspection: '2023-09-10', nextInspectionDue: '2024-03-10' }
          },
          {
            id: '3',
            make: 'Tesla',
            model: 'Model 3',
            year: 2024,
            licensePlate: 'TEL 789',
            color: 'White',
            status: 'Active',
            pricePerDay: 85,
            features: ['Autopilot', 'Bluetooth', 'AC', 'Heated Seats'],
            seats: 5,
            description: 'Premium electric vehicle from real backend API',
            verificationScore: 92,
            qualityScore: 94,
            owner: { id: '3', name: 'Real Owner 3', rating: 4.9 },
            driver: { id: '3', name: 'Real Driver 3', rating: 4.8, verified: true },
            documents: { insurance: true, registration: true, inspection: true },
            bookingHistory: { totalBookings: 28, completed: 27, cancelled: 1 },
            damageReports: { count: 0, recent: [] },
            substitutionExamples: [],
            qualityMetrics: { cleanlinessScore: 98, maintenanceScore: 96 },
            inspectionDetails: { lastInspection: '2023-12-05', nextInspectionDue: '2024-06-05' }
          }
        ];
        
        setVehicles(mockVehicles);
        setVehicleStats({
          totalVehicles: 3,
          activeVehicles: 2,
          rentedVehicles: 1,
          pendingVehicles: 0,
          revenue: 15420,
          utilizationRate: 67
        });
      } else {
        // Local development - use real API
        const [vehiclesResponse, statsResponse] = await Promise.all([
          vehicleAPI.getVehicles({ page: 1, limit: 50 }),
          vehicleAPI.getDashboardStats()
        ]);
        
        setVehicles(vehiclesResponse.vehicles);
        setVehicleStats(statsResponse);
      }
      
    } catch (error) {
      console.error('Error loading vehicle data:', error);
      // You could add a proper error state here
    } finally {
      setLoading(false);
    }
  }, []);

  // Load data on component mount
  useEffect(() => {
    loadVehicleData();
  }, [loadVehicleData]);

  // Vehicle data is now loaded from API via loadVehicleData function

  // Apply filters to vehicle list
  const filteredVehicles = useMemo(() => {
    let filtered = vehicles;

    // Search text filter
    if (activeFilters.searchText && activeFilters.searchText.trim() !== '') {
      const searchTerm = activeFilters.searchText.toLowerCase();
      filtered = filtered.filter(vehicle => 
        vehicle.make.toLowerCase().includes(searchTerm) ||
        vehicle.model.toLowerCase().includes(searchTerm) ||
        vehicle.licensePlate.toLowerCase().includes(searchTerm) ||
        vehicle.vin?.toLowerCase().includes(searchTerm) ||
        vehicle.color?.toLowerCase().includes(searchTerm)
      );
    }

    // Status filter
    if (activeFilters.status.length > 0) {
      filtered = filtered.filter(vehicle => 
        activeFilters.status.some(status => 
          vehicle.status.toLowerCase() === status.toLowerCase()
        )
      );
    }

    // Type filter
    if (activeFilters.type.length > 0) {
      filtered = filtered.filter(vehicle => 
        activeFilters.type.some(type => 
          vehicle.type?.toLowerCase() === type.toLowerCase()
        )
      );
    }

    // Price range filter
    filtered = filtered.filter(vehicle => 
      vehicle.pricePerDay >= activeFilters.priceRange[0] && 
      vehicle.pricePerDay <= activeFilters.priceRange[1]
    );

    // Year range filter
    filtered = filtered.filter(vehicle => 
      vehicle.year >= activeFilters.yearRange[0] && 
      vehicle.year <= activeFilters.yearRange[1]
    );

    // Seats filter
    if (activeFilters.seats !== undefined) {
      filtered = filtered.filter(vehicle => vehicle.seats === activeFilters.seats);
    }

    // Features filter
    if (activeFilters.features.length > 0) {
      filtered = filtered.filter(vehicle => 
        activeFilters.features.every(feature => 
          vehicle.features?.includes(feature)
        )
      );
    }

    // Verification score filter
    if (activeFilters.minVerificationScore !== undefined) {
      filtered = filtered.filter(vehicle => 
        vehicle.verificationScore >= activeFilters.minVerificationScore!
      );
    }

    // Quality score filter
    if (activeFilters.minQualityScore !== undefined) {
      filtered = filtered.filter(vehicle => 
        vehicle.qualityScore >= activeFilters.minQualityScore!
      );
    }

    // Documents filter
    if (activeFilters.hasDocuments === true) {
      filtered = filtered.filter(vehicle => 
        vehicle.documents?.insurance && 
        vehicle.documents?.registration && 
        vehicle.documents?.inspection
      );
    }

    return filtered;
  }, [vehicles, activeFilters]);

  const searchFilteredVehicles = useMemo(() => {
    return filteredVehicles.filter(vehicle => {
      const searchLower = searchTerm.toLowerCase();
      return vehicle.make.toLowerCase().includes(searchLower) ||
             vehicle.model.toLowerCase().includes(searchLower) ||
             vehicle.vin?.toLowerCase().includes(searchLower) ||
             vehicle.licensePlate.toLowerCase().includes(searchLower) ||
             vehicle.driver?.name.toLowerCase().includes(searchLower);
    });
  }, [filteredVehicles, searchTerm]);

  const paginatedVehicles = useMemo(() => {
    const start = page * itemsPerPage;
    return searchFilteredVehicles.slice(start, start + itemsPerPage);
  }, [searchFilteredVehicles, page]);

  const stats = useMemo(() => ({
    totalListings: searchFilteredVehicles.length,
    activeListings: searchFilteredVehicles.filter(v => v.status === 'Active').length,
    rentedVehicles: searchFilteredVehicles.filter(v => v.status === 'Rented').length,
    pendingVerification: searchFilteredVehicles.filter(v => v.status === 'Pending').length,
  }), [searchFilteredVehicles]);

  const handleViewDetails = useCallback((vehicle: Vehicle) => {
    setSelectedVehicle(vehicle);
    setDetailDialogOpen(true);
  }, []);

  const handleAddVehicle = useCallback(() => {
    setAddVehicleOpen(true);
  }, []);

  const handleBulkUpload = useCallback(() => {
    setBulkUploadOpen(true);
  }, []);

  const handleFilter = useCallback(() => {
    setFilterDialogOpen(true);
  }, []);

  const applyFilters = useCallback((filters: VehicleFilters) => {
    setActiveFilters(filters);
  }, []);

  const clearFilters = useCallback(() => {
    setActiveFilters({
      status: [],
      type: [],
      priceRange: [0, 500],
      yearRange: [2000, new Date().getFullYear()],
      features: [],
      searchText: ''
    });
  }, []);

  const handleExport = useCallback(() => {
    // TODO: Implement Export functionality (CSV, Excel, PDF)
    const csvData = vehicles.map(v => ({
      VIN: v.vin,
      Make: v.make,
      Model: v.model,
      Year: v.year,
      LicensePlate: v.licensePlate,
      Status: v.status,
      Owner: v.owner.name,
      Driver: v.driver.name,
      PricePerDay: v.pricePerDay,
      VerificationScore: v.verificationScore,
      QualityScore: v.qualityScore
    }));
    
    const csvContent = 'data:text/csv;charset=utf-8,' + 
      Object.keys(csvData[0]).join(',') + '\n' +
      csvData.map(row => Object.values(row).join(',')).join('\n');
    
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `vehicles-export-${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, [vehicles]);

  const handleManageVehicle = useCallback((vehicle: Vehicle) => {
    // Open edit dialog for vehicle management
    setEditingVehicle(vehicle);
    setEditVehicleOpen(true);
  }, []);

  const handleScheduleInspection = useCallback((vehicle: Vehicle) => {
    setInspectionVehicle(vehicle);
    setScheduleInspectionOpen(true);
  }, []);

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: theme.palette.background.default }}>
      {/* Header */}
      <Box sx={{ 
        p: 3, 
        borderBottom: `1px solid ${theme.palette.divider}`,
        bgcolor: 'background.paper' 
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: theme.palette.text.primary }}>
              Vehicle Operations
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage vehicle listings, quality, and performance
            </Typography>
          </Box>
          <Stack direction="row" spacing={2}>
            <Button
              variant="outlined"
              startIcon={<FilterList />}
              onClick={handleFilter}
            >
              Filter
            </Button>
            <Button
              variant="outlined"
              startIcon={<Download />}
              onClick={handleExport}
            >
              Export
            </Button>
            <Button
              variant="outlined"
              startIcon={<Upload />}
              onClick={handleBulkUpload}
            >
              Bulk Upload
            </Button>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={handleAddVehicle}
            >
              Add Vehicle
            </Button>
          </Stack>
        </Box>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ p: 3 }}>
        {[
          { label: 'Total Listings', value: stats.totalListings, icon: DirectionsCar, trend: '+4.8%', color: theme.palette.primary.main },
          { label: 'Active', value: stats.activeListings, icon: CheckCircle, trend: '+2.5%', color: theme.palette.success.main },
          { label: 'Rented', value: stats.rentedVehicles, icon: Speed, trend: '+12%', color: '#3b82f6' },
          { label: 'Pending', value: stats.pendingVerification, icon: Schedule, trend: '0%', color: theme.palette.warning.main },
        ].map((stat, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Fade in={!loading} timeout={index * 200}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      {loading ? (
                        <>
                          <Skeleton variant="text" width={100} height={40} />
                          <Skeleton variant="text" width={80} height={20} />
                        </>
                      ) : (
                        <>
                          <Typography variant="h4" sx={{ fontWeight: 700 }}>
                            {stat.value}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {stat.label}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                            <TrendingUp sx={{ fontSize: 16, color: stat.color, mr: 0.5 }} />
                            <Typography variant="caption" sx={{ color: stat.color, fontWeight: 600 }}>
                              {stat.trend}
                            </Typography>
                          </Box>
                        </>
                      )}
                    </Box>
                    <Avatar sx={{ bgcolor: `${stat.color}20`, color: stat.color }}>
                      <stat.icon />
                    </Avatar>
                  </Box>
                </CardContent>
              </Card>
            </Fade>
          </Grid>
        ))}
      </Grid>

      {/* Search and View Toggle */}
      <Box sx={{ px: 3, pb: 3 }}>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
              <TextField
                placeholder="Search by make, model, VIN, license plate, or driver..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                size="small"
                sx={{ flexGrow: 1, maxWidth: 500 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
              />
              <Stack direction="row" spacing={1}>
                <IconButton 
                  onClick={() => setViewMode('grid')}
                  color={viewMode === 'grid' ? 'primary' : 'default'}
                >
                  <ViewModule />
                </IconButton>
                <IconButton 
                  onClick={() => setViewMode('list')}
                  color={viewMode === 'list' ? 'primary' : 'default'}
                >
                  <ViewList />
                </IconButton>
              </Stack>
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Vehicle Grid */}
      <Box sx={{ px: 3, pb: 3 }}>
        {loading ? (
          <Grid container spacing={3}>
            {[...Array(8)].map((_, index) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
                <Card>
                  <Skeleton variant="rectangular" height={200} />
                  <CardContent>
                    <Skeleton variant="text" width="60%" height={32} />
                    <Skeleton variant="text" width="40%" />
                    <Box sx={{ mt: 2 }}>
                      <Skeleton variant="circular" width={48} height={48} />
                    </Box>
                    <Box sx={{ mt: 2 }}>
                      <Skeleton variant="rectangular" height={4} />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        ) : (
          <>
            <Grid container spacing={3}>
              {paginatedVehicles.map((vehicle) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={vehicle.id}>
                  <VehicleCard 
                    vehicle={vehicle} 
                    onViewDetails={() => handleViewDetails(vehicle)}
                    onHostClick={(hostId) => {
                      setSelectedHostId(hostId);
                      setShowHostProfile(true);
                    }}
                    onManageVehicle={handleManageVehicle}
                  />
                </Grid>
              ))}
            </Grid>

            {/* Pagination */}
            {filteredVehicles.length > itemsPerPage && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                <Stack direction="row" spacing={2}>
                  <Button
                    variant="outlined"
                    onClick={() => setPage(p => Math.max(0, p - 1))}
                    disabled={page === 0}
                    startIcon={<NavigateBefore />}
                  >
                    Previous
                  </Button>
                  <Box sx={{ display: 'flex', alignItems: 'center', px: 3 }}>
                    <Typography>
                      Page {page + 1} of {Math.ceil(filteredVehicles.length / itemsPerPage)}
                    </Typography>
                  </Box>
                  <Button
                    variant="outlined"
                    onClick={() => setPage(p => p + 1)}
                    disabled={page >= Math.ceil(filteredVehicles.length / itemsPerPage) - 1}
                    endIcon={<NavigateNext />}
                  >
                    Next
                  </Button>
                </Stack>
              </Box>
            )}
          </>
        )}
      </Box>

      {/* Vehicle Detail Dialog */}
      <VehicleDetailDialog
        open={detailDialogOpen}
        onClose={() => setDetailDialogOpen(false)}
        vehicle={selectedVehicle}
      />

      {/* Host Profile Dialog */}
      <Dialog open={showHostProfile} onClose={() => setShowHostProfile(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {(() => {
              const hostData = vehicles.find(v => v.owner?.id === selectedHostId)?.owner;
              return hostData ? (
                <>
                  <Avatar src={hostData.profileImage || hostData.avatar} sx={{ width: 56, height: 56 }}>
                    {hostData.name?.split(' ').map(n => n[0]).join('')}
                  </Avatar>
                  <Box>
                    <Typography variant="h6">
                      {hostData.name}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Star sx={{ color: 'warning.main', fontSize: 16 }} />
                      <Typography variant="body2" color="text.secondary">
                        {hostData.rating}/5.0 • {hostData.totalVehicles} vehicles
                      </Typography>
                    </Box>
                  </Box>
                </>
              ) : null;
            })()}
          </Box>
        </DialogTitle>
        <DialogContent>
          {(() => {
            const hostData = vehicles.find(v => v.owner?.id === selectedHostId)?.owner;
            const hostVehicles = vehicles.filter(v => v.owner?.id === selectedHostId);
            
            return hostData ? (
              <Box sx={{ mt: 2 }}>
                {/* Host Details */}
                <Grid container spacing={3}>
                  <Grid item xs={12} md={4}>
                    <Card>
                      <CardContent>
                        <Typography variant="h6" sx={{ mb: 2 }}>Host Information</Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Person sx={{ color: 'text.secondary', fontSize: 20 }} />
                            <Box>
                              <Typography variant="body2" color="text.secondary">Email</Typography>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {hostData.email}
                              </Typography>
                            </Box>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CalendarToday sx={{ color: 'text.secondary', fontSize: 20 }} />
                            <Box>
                              <Typography variant="body2" color="text.secondary">Joined</Typography>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {hostData.joinedDate ? new Date(hostData.joinedDate).toLocaleDateString() : 'N/A'}
                              </Typography>
                            </Box>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <DirectionsCar sx={{ color: 'text.secondary', fontSize: 20 }} />
                            <Box>
                              <Typography variant="body2" color="text.secondary">Total Vehicles</Typography>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {hostData.totalVehicles}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                  
                  {/* Host's Vehicles */}
                  <Grid item xs={12} md={8}>
                    <Card>
                      <CardContent>
                        <Typography variant="h6" sx={{ mb: 2 }}>
                          Vehicle Listings ({hostVehicles.length})
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                          {hostVehicles.map((vehicle) => (
                            <Paper key={vehicle.id} sx={{ p: 2, border: 1, borderColor: 'divider' }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                  <Avatar 
                                    sx={{ width: 48, height: 48, borderRadius: 2 }}
                                    variant="rounded"
                                    src={vehicle.primaryImage}
                                  >
                                    <DirectionsCar />
                                  </Avatar>
                                  <Box>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                      {vehicle.make} {vehicle.model} ({vehicle.year})
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {vehicle.licensePlate} • VIN: {vehicle.vin}
                                    </Typography>
                                  </Box>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                  <Box sx={{ textAlign: 'right' }}>
                                    <Chip
                                      label={vehicle.status}
                                      size="small"
                                      sx={{
                                        bgcolor: getStatusColor(vehicle.status.toLowerCase()),
                                        color: 'white',
                                        fontWeight: 600
                                      }}
                                    />
                                    <Typography variant="caption" display="block" color="text.secondary">
                                      ${vehicle.pricePerDay}/day
                                    </Typography>
                                  </Box>
                                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                                    <Tooltip title="View Details">
                                      <IconButton 
                                        size="small" 
                                        onClick={() => {
                                          setShowHostProfile(false);
                                          handleViewDetails(vehicle);
                                        }}
                                      >
                                        <Visibility />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Edit Vehicle">
                                      <IconButton 
                                        size="small"
                                        onClick={() => {
                                          setEditingVehicle(vehicle);
                                          setEditVehicleOpen(true);
                                        }}
                                      >
                                        <Edit />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Schedule Inspection">
                                      <IconButton 
                                        size="small"
                                        onClick={() => {
                                          setShowHostProfile(false);
                                          handleScheduleInspection(vehicle);
                                        }}
                                      >
                                        <Schedule />
                                      </IconButton>
                                    </Tooltip>
                                  </Box>
                                </Box>
                              </Box>
                            </Paper>
                          ))}
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </Box>
            ) : (
              <Typography>Host not found</Typography>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowHostProfile(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Add Vehicle Dialog */}
      <AddVehicleDialog 
        open={addVehicleOpen} 
        onClose={() => setAddVehicleOpen(false)}
        onSubmit={async (vehicleData) => {
          try {
            const result = await vehicleAPI.createVehicle(vehicleData);
            
            // In production, add the new vehicle to the current list for demo
            if (import.meta.env.MODE === 'production') {
              const newVehicle = {
                id: result.id,
                make: vehicleData.make,
                model: vehicleData.model,
                year: vehicleData.year,
                licensePlate: vehicleData.licensePlate,
                color: vehicleData.color || 'Unknown',
                status: 'Active',
                pricePerDay: vehicleData.dailyRate,
                features: vehicleData.features || [],
                seats: vehicleData.seats || 5,
                description: vehicleData.description || 'New vehicle',
                verificationScore: 85,
                qualityScore: 90,
                owner: { id: 'new', name: 'New Owner', rating: 4.5 },
                driver: { id: 'new', name: 'New Driver', rating: 4.5, verified: true },
                documents: { insurance: true, registration: true, inspection: true },
                bookingHistory: { totalBookings: 0, completed: 0, cancelled: 0 },
                damageReports: { count: 0, recent: [] },
                substitutionExamples: [],
                qualityMetrics: { cleanlinessScore: 95, maintenanceScore: 92 },
                inspectionDetails: { lastInspection: new Date().toISOString().split('T')[0], nextInspectionDue: '2024-12-31' }
              };
              setVehicles(prev => [newVehicle, ...prev]);
            } else {
              // Local development - refresh from real API
              await loadVehicleData();
            }
            
            setAddVehicleOpen(false);
            alert('Vehicle added successfully!');
          } catch (error) {
            console.error('Error creating vehicle:', error);
            alert(`Error creating vehicle: ${error.message || 'Unknown error'}`);
            throw error;
          }
        }}
      />

      {/* Bulk Upload Dialog */}
      <BulkVehicleUploadMUI 
        open={bulkUploadOpen} 
        onClose={() => setBulkUploadOpen(false)}
        onUpload={async (vehiclesToUpload) => {
          try {
            // Create all vehicles via API
            const promises = vehiclesToUpload.map(vehicleData => vehicleAPI.createVehicle(vehicleData));
            const results = await Promise.all(promises);
            
            // In production, add the new vehicles to the current list for demo
            if (import.meta.env.MODE === 'production') {
              const newVehicles = vehiclesToUpload.map((vehicleData, index) => ({
                id: results[index]?.id || Date.now().toString() + index,
                make: vehicleData.make,
                model: vehicleData.model,
                year: vehicleData.year,
                licensePlate: vehicleData.licensePlate,
                color: vehicleData.color || 'Unknown',
                status: 'Active',
                pricePerDay: vehicleData.dailyRate,
                features: vehicleData.features || [],
                seats: vehicleData.seats || 5,
                description: vehicleData.description || 'Bulk uploaded vehicle',
                verificationScore: 80,
                qualityScore: 85,
                owner: { id: 'bulk', name: 'Bulk Owner', rating: 4.5 },
                driver: { id: 'bulk', name: 'Bulk Driver', rating: 4.5, verified: true },
                documents: { insurance: true, registration: true, inspection: true },
                bookingHistory: { totalBookings: 0, completed: 0, cancelled: 0 },
                damageReports: { count: 0, recent: [] },
                substitutionExamples: [],
                qualityMetrics: { cleanlinessScore: 90, maintenanceScore: 88 },
                inspectionDetails: { lastInspection: new Date().toISOString().split('T')[0], nextInspectionDue: '2024-12-31' }
              }));
              setVehicles(prev => [...newVehicles, ...prev]);
            } else {
              // Local development - refresh from real API
              await loadVehicleData();
            }
            
            setBulkUploadOpen(false);
            alert(`Successfully uploaded ${vehiclesToUpload.length} vehicles!`);
          } catch (error) {
            console.error('Error uploading vehicles:', error);
            throw error;
          }
        }}
      />

      {/* Edit Vehicle Dialog */}
      <EditVehicleDialog 
        open={editVehicleOpen} 
        onClose={() => {
          setEditVehicleOpen(false);
          setEditingVehicle(null);
        }}
        vehicle={editingVehicle}
        onSave={async (vehicleData) => {
          try {
            if (!editingVehicle) return;
            
            // Convert form data to API format
            const updateData = {
              vin: vehicleData.vin,
              make: vehicleData.make,
              model: vehicleData.model,
              year: vehicleData.year,
              trim: vehicleData.trim,
              licensePlate: vehicleData.licensePlate,
              type: vehicleData.type,
              color: vehicleData.color,
              dailyRate: vehicleData.dailyRate,
              seats: vehicleData.seats,
              mileage: vehicleData.mileage,
              engine: vehicleData.engine,
              transmission: vehicleData.transmission,
              fuelType: vehicleData.fuelType,
              drivetrain: vehicleData.drivetrain,
              doors: vehicleData.doors,
              description: vehicleData.description,
              features: vehicleData.features,
              status: vehicleData.status
            };
            
            const result = await vehicleAPI.updateVehicle(editingVehicle.id, updateData);
            
            // Update the vehicles list with the updated vehicle
            setVehicles(prev => prev.map(v => 
              v.id === editingVehicle.id 
                ? {
                    ...v,
                    ...updateData,
                    pricePerDay: vehicleData.dailyRate,
                    status: vehicleData.status.charAt(0).toUpperCase() + vehicleData.status.slice(1)
                  }
                : v
            ));
            
            setEditVehicleOpen(false);
            setEditingVehicle(null);
            alert('Vehicle updated successfully!');
          } catch (error) {
            console.error('Error updating vehicle:', error);
            alert(`Error updating vehicle: ${error.message || 'Unknown error'}`);
            throw error;
          }
        }}
      />

      {/* Filter Vehicles Dialog */}
      <FilterVehiclesDialog 
        open={filterDialogOpen} 
        onClose={() => setFilterDialogOpen(false)}
        onApplyFilters={applyFilters}
        onClearFilters={clearFilters}
        currentFilters={activeFilters}
      />

      {/* Schedule Inspection Dialog */}
      <ScheduleInspectionDialog 
        open={scheduleInspectionOpen} 
        onClose={() => {
          setScheduleInspectionOpen(false);
          setInspectionVehicle(null);
        }}
        vehicle={inspectionVehicle}
        onSchedule={async (inspectionData) => {
          try {
            // In production, simulate API call
            if (import.meta.env.MODE === 'production') {
              await new Promise(resolve => setTimeout(resolve, 1000));
              alert(`Inspection scheduled for ${inspectionData.scheduledDate} at ${inspectionData.scheduledTime}`);
            } else {
              // Call real API in development
              // await inspectionAPI.scheduleInspection(inspectionData);
              alert(`Inspection scheduled for ${inspectionData.scheduledDate} at ${inspectionData.scheduledTime}`);
            }
            
            setScheduleInspectionOpen(false);
            setInspectionVehicle(null);
          } catch (error) {
            console.error('Error scheduling inspection:', error);
            throw error;
          }
        }}
      />
    </Box>
  );
};

export default VehiclesPageOptimized;